import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import Chip from '@opsbase/web-chips/Chip';
import GradeChip from '@opsbase/web-chips/GradeChip';
import PassScoreChip from '@opsbase/web-chips/PassScoreChip';
import PassStatusChip from '@opsbase/web-chips/PassStatusChip';
import ScoreChip from '@opsbase/web-chips/ScoreChip';
import useCss from '@opsbase/web-css';
import DeleteIcon from '@opsbase/web-icons/icons/DeleteIcon';
import LateIcon from '@opsbase/web-icons/icons/LateIcon';
import IssueCountIcon from '@opsbase/web-icons/IssueCountIcon';
import Card from '@opsbase/web-layout/Card';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import Button from '@opsbase/web-ui/Button';
import PercentCircle from '@opsbase/web-ui/PercentCircle';
import Text from '@opsbase/web-ui/Text';
import Tooltip from '@opsbase/web-ui/Tooltip';
import React, { useCallback, useMemo } from 'react';
const makeMyClasses = (props) => ({
    root: {
        overflow: 'visible',
        opacity: props.isClosed ? 0.8 : 1,
        maxWidth: 260,
        minWidth: 260,
        minHeight: 260,
        maxHeight: 260,
        boxShadow: props.isHighlighted ? `0 0 12px -4px ${Palette.BlackCoral.main}` : undefined,
        borderColor: props.statusColor.main,
        backgroundColor: props.isClosed ? 'transparent' : Palette.White.main,
        position: 'relative',
        paddingTop: 32,
        margin: '0 0 16px 16px',
        transition: 'borderColor 0.2s, background 0.2s, box-shadow 0.2s',
        '&:hover': {
            boxShadow: `4px 4px 18px -4px ${Palette.BlackCoral.main}`,
            backgroundColor: Palette.LightBlue.main,
        },
    },
    inner: {
        position: 'relative',
        zIndex: 10,
    },
    topPane: {
        maxHeight: 142,
        overflow: 'hidden',
    },
    watermark: {
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        bottom: 0,
        opacity: 0.1,
        zIndex: 9,
        overflow: 'hidden',
    },
    watermarkInner: {
        position: 'relative',
        right: -24,
        bottom: -24,
    },
    titleLine: {
        fontSize: 12,
        lineHeight: '14px',
        fontStyle: 'italic',
        color: Palette.GreyBlue.main,
    },
    titleMain: {
        fontSize: 18,
        lineHeight: '20px',
        marginRight: 8,
        fontWeight: 700,
        fontStyle: 'normal',
        color: Palette.Black.main,
        display: 'block',
    },
    titleMainPane: {
        maxHeight: 98,
        overflow: 'hidden',
    },
    due: {
        fontSize: 16,
        lineHeight: '16px',
        color: props.dueColor.main,
    },
    startable: {
        fontSize: 12,
        lineHeight: '14px',
        color: props.dueColor.main,
    },
    assigned: {
        fontSize: 12,
        lineHeight: '14px',
        color: Palette.GreyBlue.main,
    },
    updated: {
        minHeight: 14,
        fontSize: 12,
        lineHeight: '14px',
        color: Palette.GreyBlue.main,
    },
    place: {
        fontSize: 12,
        lineHeight: '14px',
    },
    asset: {
        fontSize: 12,
        lineHeight: '14px',
    },
    status: {
        position: 'absolute',
        textTransform: 'uppercase',
        backgroundColor: props.statusColor.main,
        color: props.statusColor.contrastText,
        textAlign: 'center',
        padding: '4px 8px',
        top: 0,
        left: 0,
        right: 0,
        borderRadius: '8px 8px 0 0',
    },
    stamp: {
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: 11,
        transform: 'rotate(-45deg)',
        transformOrigin: 'top left',
        top: 'calc(100% - 20px)',
        left: 0,
        width: '120%',
        height: 20,
        fontSize: 40,
        lineHeight: '40px',
        color: props.statusColor.main,
        opacity: 0.15,
        textAlign: 'center',
    },
    cancelButton: {
        position: 'absolute',
        bottom: -1,
        right: -1,
        borderRadius: '8px 0',
        zIndex: 100,
    },
    iconBox: {
        position: 'absolute',
        top: -6,
    },
});
export function ToDoCard(props) {
    const { onClick, onCancel, ...data } = props;
    const self = useSelf();
    const handleCancel = useCallback(e => {
        e.stopPropagation();
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    }, [onCancel]);
    const myCss = useCss(makeMyClasses, [data]);
    const scoreGradePassBit = useMemo(() => {
        if (!(data === null || data === void 0 ? void 0 : data.scoreGradePass))
            return null;
        const hasStatus = !!data.scoreGradePass.passStatus;
        const hasScore = !!data.scoreGradePass.max;
        const hasGrade = !!data.scoreGradePass.grade;
        return (React.createElement(Horizontal, { gutter: true, shrink: true, justify: "flex-start" },
            hasScore && hasStatus && (React.createElement(PassScoreChip, { scoreMax: data.scoreGradePass.max, scoreTotal: data.scoreGradePass.total, status: data.scoreGradePass.passStatus })),
            hasScore && !hasStatus && (React.createElement(ScoreChip, { scoreMax: data.scoreGradePass.max, scoreTotal: data.scoreGradePass.total })),
            hasStatus && !hasScore && (React.createElement(PassStatusChip, { status: data.scoreGradePass.passStatus })),
            hasGrade && React.createElement(GradeChip, { grade: data.scoreGradePass.grade })));
    }, [data, myCss]);
    return useMemo(() => {
        var _a, _b, _c, _d;
        return (React.createElement(Card, { classes: { root: myCss.root }, padding: true, onClick: onClick },
            !!data.stampLabel && React.createElement(Text, { className: myCss.stamp }, data.stampLabel),
            !!data.watermark && (React.createElement("div", { className: myCss.watermark },
                React.createElement(data.watermark, { size: 140, color: data.watermarkColor || Palette.GreyBlue, className: myCss.watermarkInner }))),
            React.createElement(Horizontal, { shrink: true, classes: { root: myCss.status }, gutter: true, centered: true },
                React.createElement(Horizontal, { shrink: true, classes: { root: myCss.iconBox }, gutter: true, centered: true },
                    data.isLate && React.createElement(LateIcon, { size: 26, color: Palette.BadRed }),
                    !!data.noOfIssues && React.createElement(IssueCountIcon, { count: data.noOfIssues, size: 26 })),
                React.createElement(Pane, { grow: true }),
                React.createElement(Text, { variant: "small" }, data.statusLabel)),
            data.isCancelable && (React.createElement(Button, { small: true, classes: { root: myCss.cancelButton }, icon: React.createElement(DeleteIcon, { size: 16 }), color: Palette.BadRed, onClick: handleCancel })),
            React.createElement(Vertical, { gutter: true, classes: { root: myCss.inner } },
                React.createElement(Vertical, { gutter: true, classes: { root: myCss.topPane }, justify: "flex-start" },
                    React.createElement(Pane, { classes: { root: myCss.titleMainPane } },
                        React.createElement(Text, { className: myCss.titleMain }, data.title)),
                    !!data.place && React.createElement(Text, { className: myCss.place }, data.place),
                    !!data.asset && React.createElement(Text, { className: myCss.asset }, data.asset),
                    !!data.description && (React.createElement(Text, { variant: "h2", className: myCss.titleLine }, data.description))),
                !!data.scoreGradePass && scoreGradePassBit,
                React.createElement(Horizontal, { gutter: true, shrink: true },
                    React.createElement(Vertical, { gutter: true },
                        !!((_a = data.tags) === null || _a === void 0 ? void 0 : _a.length) && (React.createElement(Horizontal, { gutter: true, justify: "flex-start" }, data.tags.map((tag, idx) => (React.createElement(Chip, { style: { maxWidth: 160 }, key: 'tag' + idx, small: true, color: Palette.Grey, label: tag }))))),
                        !!data.dueLabel && (React.createElement(Tooltip, { tip: data.dueTip },
                            React.createElement(Text, { className: myCss.due }, data.dueLabel))),
                        !!data.startableLabel && (React.createElement(Text, { className: myCss.startable }, data.startableLabel))),
                    !!data.percComplete && (React.createElement(Pane, { shrink: true },
                        React.createElement(PercentCircle, { percent: data.percComplete, label: data.percComplete + '%' })))),
                !!data.assignedTo && (React.createElement(Text, { className: myCss.assigned },
                    React.createElement("span", null,
                        i18n.t('assigned-to'),
                        " "),
                    data.assignedTo.all && i18n.lc('anyone'),
                    [
                        ...(_b = data.assignedTo.staff) === null || _b === void 0 ? void 0 : _b.map(s => s.id === self.id ? i18n.lc('you') : s.title),
                        ...(_c = data.assignedTo.teams) === null || _c === void 0 ? void 0 : _c.map(s => s.title),
                        ...(_d = data.assignedTo.positions) === null || _d === void 0 ? void 0 : _d.map(s => s.title),
                    ].join(', '))),
                !!data.updatedLabel && (React.createElement(Tooltip, { tip: data.updatedTip },
                    React.createElement(Text, { className: myCss.updated }, data.updatedLabel))))));
    }, [myCss, onClick, data, handleCancel, scoreGradePassBit, self.id]);
}
export default ToDoCard;
